import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiSun, FiMoon, FiEye, FiWifi, FiCoffee, FiShield } = FiIcons;

const Experience = () => {
  const features = [
    {
      icon: FiSun,
      title: 'Dawn Chorus Experience',
      description: 'Join the 5:00 AM bird wave as the forest awakens with endemic calls',
      color: 'from-yellow-400 to-orange-500'
    },
    {
      icon: FiEye,
      title: 'Endemic Species Guarantee',
      description: 'Access to 34 endemic species including the elusive Serendib Scops Owl',
      color: 'from-emerald-400 to-teal-500'
    },
    {
      icon: FiMoon,
      title: 'Nocturnal Safaris',
      description: 'Night walks to spot Sri Lanka Frogmouth and other nocturnal specialists',
      color: 'from-purple-400 to-indigo-500'
    },
    {
      icon: FiWifi,
      title: 'Modern Comfort',
      description: 'AC suites, WiFi, and pool - luxury amenities in the heart of nature',
      color: 'from-blue-400 to-cyan-500'
    },
    {
      icon: FiCoffee,
      title: 'Birder\'s Breakfast',
      description: 'Pre-dawn coffee service and packed meals for early morning expeditions',
      color: 'from-amber-400 to-yellow-500'
    },
    {
      icon: FiShield,
      title: 'Expert Guides',
      description: 'Specialized local guides with intimate knowledge of the Ullinduwawa border',
      color: 'from-green-400 to-emerald-500'
    }
  ];

  const bigFive = [
    {
      name: 'Serendib Scops Owl',
      scientific: 'Otus thilohoffmanni',
      status: 'The Rarity',
      description: 'Only discovered in 2001. Daytime roosts near Kudawe Research Center.'
    },
    {
      name: 'Sri Lanka Blue Magpie',
      scientific: 'Urocissa ornata',
      status: 'The Icon',
      description: 'Often visits lodge gardens. Experience "Breakfast with Magpies".'
    },
    {
      name: 'Red-faced Malkoha',
      scientific: 'Phaenicophaeus pyrrhocephalus',
      status: 'The Canopy Ghost',
      description: 'High in the trees. Best seen from the wide trails of Kudawe.'
    },
    {
      name: 'Sri Lanka Frogmouth',
      scientific: 'Batrachostomus moniliger',
      status: 'The Invisible',
      description: 'Masters of camouflage in bamboo thickets near Kudawe river.'
    },
    {
      name: 'White-faced Starling',
      scientific: 'Sturnornis albofrontatus',
      status: 'The Canopy Dweller',
      description: 'Often associated with Bird Waves. High-priority for listers.'
    }
  ];

  return (
    <section id="experience" className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-playfair">
            The Complete Experience
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Where scientific birding meets luxury comfort at the world's premier bird wave location
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="glass rounded-2xl p-8 hover-lift group cursor-pointer"
            >
              <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                <SafeIcon icon={feature.icon} className="text-white text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4 group-hover:text-emerald-300 transition-colors">
                {feature.title}
              </h3>
              <p className="text-gray-300 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Big Five Endemics */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center font-playfair">
            The "Big Five" Endemics of Jansen's Bungalow
          </h3>
          <div className="grid lg:grid-cols-2 gap-8">
            {bigFive.map((bird, index) => (
              <motion.div
                key={bird.name}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass rounded-2xl p-8 hover-lift group cursor-pointer"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="text-xl font-bold text-white group-hover:text-emerald-300 transition-colors">
                      {bird.name}
                    </h4>
                    <p className="text-gray-400 italic text-sm">{bird.scientific}</p>
                  </div>
                  <span className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                    {bird.status}
                  </span>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  {bird.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <div className="glass rounded-2xl p-12 bg-gradient-to-r from-emerald-900/30 to-teal-900/30">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-6 font-playfair">
              Ready for the Ultimate Birding Adventure?
            </h3>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Book your specialized birding package and experience the Sinharaja Bird Wave 
              from the comfort of Sri Lanka's premier ornithological retreat.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-8 py-4 rounded-full font-semibold text-lg hover-lift"
              >
                Book Birding Package
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="glass text-white px-8 py-4 rounded-full font-semibold text-lg hover-lift border border-white/20"
              >
                Contact Our Birding Specialist
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Experience;